<template>
  <div class="layout-sidebar-section">
    <div
      class="layout-sidebar-section-title"
      @click="$emit('toggleSection', 'transformSection')"
    >
      <strong>Transform</strong>
      <b-icon :icon="isOpen.transformSection ? 'chevron-up' : 'chevron-down'" />
    </div>
    <transition>
      <b-collapse :visible="isOpen.transformSection">
        <div 
          v-show="true"
        >
          <b-form-row>
            <b-col
              v-if="aspectRatioOption && showKeepAspectRatio"
              cols="12"
              class="mb-2 d-flex justify-content-between align-items-center"
            >
              <b-checkbox
                v-model="keepAspectRatioValue"
                :disabled="target.locked"
              >
                Keep aspect ratio
              </b-checkbox>
              <b-dropdown
                id="align-to-ruler-dropdown"
                variant="link"
                toggle-class="align-to-ruler-toggle"
                no-caret
                right
              >
                <template #button-content>
                  <img
                    src="/img/icons/measure.png"
                    width="24"
                    height="24"
                    alt="Align Options"
                  >
                </template>
                <b-dropdown-item @click="alignToRulerLine(RULER_ALIGN.LEFT)">
                  Align to left ruler line
                </b-dropdown-item>
                <b-dropdown-item @click="alignToRulerLine(RULER_ALIGN.RIGHT)">
                  Align to right ruler line
                </b-dropdown-item>
                <b-dropdown-item @click="alignToRulerLine(RULER_ALIGN.TOP)">
                  Align to upper ruler line
                </b-dropdown-item>
                <b-dropdown-item @click="alignToRulerLine(RULER_ALIGN.BOTTOM)">
                  Align to lower ruler line
                </b-dropdown-item>
              </b-dropdown>
            </b-col>

            <b-col cols="6">
              <b-form-group
                label="X"
                label-cols="2"
                :disabled="target.locked || !moveable"
              >
                <b-input
                  :value="xValue.toFixed(2)"
                  type="number"
                  @change="onInput('x', $event)"
                  @dblclick="selectValueOnDblclick"
                />
              </b-form-group>
            </b-col>

            <b-col cols="6">
              <b-form-group
                label="Y"
                label-cols="2"
                :disabled="target.locked || !moveable"
              >
                <b-input
                  :value="yValue.toFixed(2)"
                  type="number"
                  @change="onInput('y', $event)"
                  @dblclick="selectValueOnDblclick"
                />
              </b-form-group>
            </b-col>

            <b-col
              v-if="showSize"
              cols="6"
            >
              <b-form-group
                label="W"
                label-cols="2"
                :disabled="target.locked || isScaleDisabled"
              >
                <b-input
                  :value="wValue.toFixed(2)"
                  type="number"
                  @change="onInput('w', $event)"
                  @dblclick="selectValueOnDblclick"
                />
              </b-form-group>
            </b-col>

            <b-col
              v-if="showSize"
              cols="6"
            >
              <b-form-group
                label="H"
                label-cols="2"
                :disabled="target.locked || isScaleDisabled"
              >
                <b-input
                  :value="hValue.toFixed(2)"
                  type="number"
                  @change="onInput('h', $event)"
                  @dblclick="selectValueOnDblclick"
                />
              </b-form-group>
            </b-col>
          </b-form-row>
        </div>
      </b-collapse>
    </transition>
  </div>
</template>

<script>
import { mapGetters } from 'vuex'
import mixinInput from "@frontend/components/input-mixins";
import {SMART_OBJECT} from "@frontend/constants/templates-field-prefixes";
import {RULER_ALIGN} from "@frontend/group/modules/ruler/constants";

const DPI = 300;
const TO_INCHES = 12;

export default {
  name: 'PositionAndSize',

  mixins: [mixinInput],

  props: {
    x: {
      type: [String, Number],
      required: true,
    },
    y: {
      type: [String, Number],
      required: true,
    },
    w: {
      type: [String, Number],
      required: true,
    },
    h: {
      type: [String, Number],
      required: true,
    },
    aspectRatioOption: {
      type: Boolean,
      default: true,
    },
    isScaleDisabled: {
      type: Boolean,
      default: false
    },
    showKeepAspectRatio: {
      type: Boolean,
      default: true
    },
    showSize: {
      type: Boolean,
      default: true
    },
    isOpen: {
      type: Object,
      default: () => ({
        transformSection: true,
      }),
    },
  },
  computed: {
    RULER_ALIGN() {
      return RULER_ALIGN
    },
    ...mapGetters('layout', [
      'keepAspectRatio',
      'target',
      'selectedInstance'
    ]),
    keepAspectRatioValue: {
      get () {
        return this.keepAspectRatio
      },
      set (val) {
        this.$store.commit('layout/SET_KEEP_ASPECT_RATIO', val)
      },
    },
    aspectRatio () {
      return this.keepAspectRatio ? this.w / this.h : null
    },
    moveable () {
      //allow moveable smartobject
      if (this.target.name === 'Product Dimensions' || (this.target.element_id && this.target.element_id.startsWith(SMART_OBJECT))) return true;
      return !this.target.templateField || this.target.templateField.isMoveable()
    },
    xValue () {
      return this.calculateInput(this.x)
    },
    yValue () {
      return this.calculateInput(this.y)
    },
    wValue () {
      return this.calculateInput(this.w)
    },
    hValue () {
      return this.calculateInput(this.h)
    },
  },
  methods: {
    onInput(key, val) {
      // let val = e.target.value
      this.$emit('input', key, this.newValToPixels(val));
      if (this.aspectRatio && this.aspectRatioOption) {
        if (key === 'w') {
          // Width has changed, calculate new height based on aspect ratio and emit both changes
          const newHeight = this.newValToPixels(val) / this.aspectRatio
          this.$emit('input', 'h', newHeight.toFixed(2))
        } else if (key === 'h') {
          // Height has changed, calculate new width based on aspect ratio and emit both changes
          const newWidth = this.newValToPixels(val) * this.aspectRatio
          this.$emit('input', 'w', newWidth.toFixed(2))
        }
      }
    },
    calculateInput(val) {
      if (this.selectedInstance?.ruler?.rulerSettings === 'in') {
        return val / DPI;
      }

      if (this.selectedInstance?.ruler?.rulerSettings === 'ft') {
        return (val / DPI) / TO_INCHES;
      }

      return val; // if setting is 'px', return the value as is
    },
    newValToPixels(val) {
      if (this.selectedInstance?.ruler?.rulerSettings === 'in') {
        return val * DPI;
      }

      if (this.selectedInstance?.ruler?.rulerSettings === 'ft') {
        return val * DPI * TO_INCHES;
      }

      return val; // if setting is 'px', return the value as is
    },
    alignToRulerLine (align) {
      const activeObjects = this.selectedInstance.canvas.getActiveObjects();
      if (activeObjects.length > 1) {
        this.selectedInstance.canvas.discardActiveObject();
        const newSelection = new fabric.ActiveSelection(
          [...activeObjects],
          { canvas: this.selectedInstance.canvas }
        );
        newSelection.setCoords();
        this.selectedInstance.canvas.setActiveObject(newSelection);
        const newActiveObjects = this.selectedInstance.canvas.getActiveObjects();
        newActiveObjects.forEach(obj => {
          const bounds = obj.getBoundingRect();
          const objCoords = obj.calcCoords();
         
          const { width, height } = bounds;
          const objX = objCoords.tl_skew.x;
          const objY = objCoords.tl_skew.y;
          const line = this.selectedInstance.ruler.getClosestLine(
            align,
            objX,
            objY,
            width,
            height
          );
         let xOffset, yOffset;
          if (line) {
            switch (align) {
              case RULER_ALIGN.LEFT:
                xOffset = objX - line.x1;
                obj.set('left', obj.left - xOffset);
                break;
              case RULER_ALIGN.RIGHT:
                xOffset = line.x1 - objX - width;
                obj.set('left', obj.left + xOffset);
                break;
              case RULER_ALIGN.TOP:
                yOffset = line.y1 - objY;
                obj.set('top', obj.top + yOffset);
                break;
              case RULER_ALIGN.BOTTOM:
                yOffset = line.y1 - objY - height;
                obj.set('top', obj.top + yOffset);
                break;
            }
          } 
        });
        this.selectedInstance.canvas.discardActiveObject();
        activeObjects.forEach(obj => {
          obj.setCoords()
        });

        const selection = new fabric.ActiveSelection(
          [...activeObjects],
          { canvas: this.selectedInstance.canvas }
        );
       
        selection.setCoords();
        this.selectedInstance.canvas.setActiveObject(selection);
        this.selectedInstance.canvas.requestRenderAll();
        return;
      }
      const line = this.selectedInstance.ruler.getClosestLine(align, this.x, this.y, this.w, this.h);
      if (line) {
        switch (align) {
          case RULER_ALIGN.LEFT:
            this.$emit('input', 'x', line.x1);
            break;
          case RULER_ALIGN.RIGHT:
            this.$emit('input', 'x', line.x1 - this.w);
            break;
          case RULER_ALIGN.TOP:
            this.$emit('input', 'y', line.y1);
            break;
          case RULER_ALIGN.BOTTOM:
            this.$emit('input', 'y', line.y1 - this.h);
            break;
        }
      }
    },
  }
}
</script>

<style scoped>
.align-to-ruler-toggle:focus {
  outline: none !important;
  box-shadow: none !important;
}


.align-to-ruler-toggle img {
  background-color: transparent !important;
}

.align-to-ruler-toggle img:hover {
  background-color: transparent !important;
}
</style>
